import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Menu, X, ChevronDown, ArrowRight,
  BookOpen, Play, Image, Mic2, FileText,
  Award, Calendar, Users, Clock, Sparkles,
  Compass, GraduationCap, Trophy, Mail, PenTool,
} from 'lucide-react'
import { resolveImageUrl } from '../../utils/imageUrl'
import { formatScheduleFallback } from '../../utils/masterclassSchedule'
import { masterclassService } from '../../services/masterclassService'

const MASTERCLASS_LINKS = [
  {
    icon: Calendar,
    label: 'Upcoming Session',
    desc: "Register for this month's masterclass",
    to: '/masterclass',
    cta: true,
  },
  {
    icon: BookOpen,
    label: 'Masterclass Archive',
    desc: 'Browse all previous sessions',
    to: '/masterclass/archive',
  },
  {
    icon: Play,
    label: 'Video Library',
    desc: 'Watch recordings & highlights',
    to: '/masterclass/videos',
  },
  {
    icon: Mic2,
    label: 'Industry Speakers',
    desc: 'Meet our guest speakers',
    to: '/masterclass/speakers',
  },
  {
    icon: Image,
    label: 'Gallery',
    desc: 'Photos from every event',
    to: '/masterclass/gallery',
  },
  {
    icon: FileText,
    label: 'Student Resources',
    desc: 'Slides, downloads & notes',
    to: '/masterclass/resources',
  },
  {
    icon: Award,
    label: 'Certificates',
    desc: 'Verify or download attendance certificates',
    to: '/verify-certificate',
  },
]

const EXPLORE_GROUPS = [
  {
    label: 'Create',
    links: [
      { icon: PenTool, label: 'Creative Tools', desc: 'Software for the work', to: '/creative-tools' },
      { icon: Trophy, label: 'Showcase', desc: 'Work made at SMA', to: '/showcase' },
    ],
  },
  {
    label: 'Learn',
    links: [
      { icon: GraduationCap, label: 'Admissions', desc: 'Intakes and how to apply', to: '/intake' },
      { icon: Image, label: 'Facilities', desc: 'Studios and classrooms', to: '/gallery' },
    ],
  },
  {
    label: 'Discover',
    links: [
      { icon: Compass, label: 'About SMA', desc: 'The academy', id: 'story' },
      { icon: Mail, label: 'Contact', desc: 'Get in touch', to: '/contact' },
    ],
  },
]

// Shared upcoming-session state across nav + badge
function useLiveNavBadge() {
  const [upcoming, setUpcoming] = useState(null)
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    if (loaded) return
    masterclassService.getUpcoming()
      .then((r) => setUpcoming(r.masterclass))
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [loaded])
  return upcoming
}

// Inline date label — merged into the Masterclass nav item, not a floating pill
function MasterclassDateInline({ upcoming }) {
  const formatShort = (d) => d && !isNaN(new Date(d).getTime())
    ? new Date(d).toLocaleDateString('en-UG', { day: 'numeric', month: 'short', timeZone: 'Africa/Kampala' })
    : null
  const dateLabel = upcoming?.event_date ? formatShort(upcoming.event_date) : null
  if (!dateLabel) return null
  return (
    <span className="hidden 2xl:inline text-cinema-gold font-sans text-[10.5px] font-semibold ml-1 whitespace-nowrap">
      · Next: {dateLabel}
    </span>
  )
}

function MasterclassMegaMenu({ visible, onClose, upcoming: upcomingProp }) {
  const [upcoming, setUpcoming] = useState(upcomingProp || null)
  const [loaded, setLoaded] = useState(!!upcomingProp)

  useEffect(() => {
    if (!visible || loaded) return
    masterclassService.getUpcoming()
      .then((r) => setUpcoming(r.masterclass))
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [visible, loaded])

  const mc = upcoming
  const formatDate = (d) => d && !isNaN(new Date(d).getTime())
    ? new Date(d).toLocaleDateString('en-UG', { weekday: 'short', day: 'numeric', month: 'short' })
    : formatScheduleFallback()

  return (
    <div
      className={`absolute top-full left-0 right-0 transition-all duration-300 ${
        visible
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 -translate-y-2 pointer-events-none'
      }`}
      style={{ zIndex: 100 }}
    >
      {/* backdrop blur strip */}
      <div className="h-px bg-gradient-to-r from-transparent via-cinema-gold/30 to-transparent" />
      <div
        className="w-full bg-cinema-charcoal border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-[1fr_320px] gap-8">

            {/* Left — navigation links */}
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-cinema-gold mb-5 font-sans font-semibold">
                The Monthly Masterclass Ecosystem
              </p>
              <div className="grid sm:grid-cols-2 gap-1">
                {MASTERCLASS_LINKS.map(({ icon: Icon, label, desc, to, cta }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={onClose}
                    className={`group flex items-start gap-4 p-3 transition-colors duration-200 ${
                      cta ? 'hover:bg-cinema-glow/10' : 'hover:bg-white/5'
                    }`}
                  >
                    <div className={`mt-0.5 p-2 shrink-0 transition-colors ${
                      cta
                        ? 'bg-cinema-glow/20 text-cinema-glow'
                        : 'bg-white/5 text-cinema-muted group-hover:bg-cinema-glow/15 group-hover:text-cinema-glow'
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className={`font-sans text-sm font-medium transition-colors ${
                        cta ? 'text-cinema-glow' : 'text-white group-hover:text-cinema-gold'
                      }`}>
                        {label}
                      </p>
                      <p className="text-cinema-muted font-sans text-xs mt-0.5 leading-snug">{desc}</p>
                    </div>
                    {cta && (
                      <ArrowRight className="w-4 h-4 text-cinema-glow ml-auto mt-1 transition-transform group-hover:translate-x-1 shrink-0" />
                    )}
                  </Link>
                ))}
              </div>
            </div>

            {/* Right — live upcoming event preview */}
            <div className="border-l border-white/6 pl-8">
              <p className="text-xs uppercase tracking-[0.25em] text-cinema-muted mb-5 font-sans font-semibold">
                Next Session
              </p>

              {!loaded ? (
                <div className="space-y-3 animate-pulse">
                  <div className="h-40 bg-white/5 rounded-none" />
                  <div className="h-3 w-3/4 bg-white/5 rounded-none" />
                  <div className="h-3 w-1/2 bg-white/5 rounded-none" />
                </div>
              ) : mc ? (
                <div className="space-y-4">
                  {/* Speaker photo */}
                  <div className="relative aspect-[3/2] overflow-hidden rounded-none bg-cinema-graphite">
                    {mc.speaker_photo ? (
                      <img
                        src={resolveImageUrl(mc.speaker_photo)}
                        alt={mc.speaker_name}
                        loading="lazy"
                        className="w-full h-full object-cover object-top"
                        onError={(e) => { e.target.style.display = 'none' }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Mic2 className="w-10 h-10 text-cinema-muted/20" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-cinema-black/80 via-transparent to-transparent" />
                    {mc.speaker_name && (
                      <div className="absolute bottom-0 inset-x-0 p-3">
                        <p className="text-white font-sans text-sm font-medium leading-tight">{mc.speaker_name}</p>
                        <p className="text-cinema-muted font-sans text-xs mt-0.5">{mc.speaker_title}{mc.speaker_company ? ` · ${mc.speaker_company}` : ''}</p>
                      </div>
                    )}
                  </div>

                  {/* Session meta */}
                  {mc.topic && (
                    <p className="text-white font-display text-sm font-light leading-snug line-clamp-2">{mc.topic}</p>
                  )}

                  <div className="space-y-1.5">
                    {[
                      [Calendar, formatDate(mc.event_date)],
                      [Clock, mc.start_time || '5:00 PM'],
                      [Users, 'FREE for Students · UGX 150K Guests'],
                    ].map(([Icon, text]) => (
                      <div key={text} className="flex items-center gap-2 text-cinema-muted font-sans text-xs">
                        <Icon className="w-3 h-3 text-cinema-gold shrink-0" />
                        {text}
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/masterclass"
                    onClick={onClose}
                    className="cinema-btn-primary w-full !text-xs !py-3 group"
                  >
                    Register Now
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Mic2 className="w-10 h-10 text-cinema-muted/20 mx-auto mb-3" />
                  <p className="text-cinema-muted font-sans text-sm">Next session coming soon</p>
                  <Link
                    to="/masterclass/archive"
                    onClick={onClose}
                    className="mt-4 cinema-btn-ghost !text-xs inline-flex"
                  >
                    Browse Archive
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Academy & Mentors are scroll-to-section links on the landing page,
// now accessed via the Explore dropdown (EXPLORE_LINKS) instead of primary nav.

function isInternalIntakeRecord(intake) {
  if (!intake) return false
  const haystack = [intake.name, intake.shortLabel, intake.navLabel, intake.code]
    .filter(Boolean)
    .join(' ')
  return /\b(debug|test|dummy|sample|placeholder|tmp|temp)\b/i.test(haystack)
}

export default function CinematicNav({ scrolled, navOverHero, onNavigate, intake = null, fallback = false }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [megaOpen, setMegaOpen] = useState(false)
  const [exploreOpen, setExploreOpen] = useState(false)
  const navUpcoming = useLiveNavBadge()
  const [mcMobileOpen, setMcMobileOpen] = useState(false)
  const [exploreMobileOpen, setExploreMobileOpen] = useState(false)
  // Intake-aware nav link target. When there is no active intake we
  // still surface the link, but point it at the evergreen Programs catalogue.
  const intakeHref = (!fallback && intake && (intake.intakePageHref || '/intake')) || '/programs'
  const hasIntake = !fallback && intake && intake.status !== 'none'
  const intakeIsInternal = isInternalIntakeRecord(intake)
  const publicIntakeLabel = hasIntake && !intakeIsInternal ? intake.shortLabel : null
  const intakeSubtext = publicIntakeLabel
    ? `${publicIntakeLabel.toLowerCase().replace(' intake', '')} intake open`
    : null
  const megaRef = useRef(null)
  const megaMenuRef = useRef(null)
  const megaBtnRef = useRef(null)
  const exploreRef = useRef(null)
  const exploreMenuRef = useRef(null)
  const exploreBtnRef = useRef(null)
  const closeTimeout = useRef(null)
  const exploreCloseTimeout = useRef(null)
  const location = useLocation()
  const navigate = useNavigate()

  const isMasterclassSection = location.pathname.startsWith('/masterclass') ||
    location.pathname.startsWith('/speakers') ||
    location.pathname === '/verify-certificate'

  const isProjectLabSection = location.pathname.startsWith('/project-lab')

  const isExploreSection = location.pathname === '/showcase' ||
    location.pathname === '/contact' || location.pathname === '/explore' ||
    location.pathname === '/gallery' ||
    location.pathname.startsWith('/creative-tools')

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  const closeMega = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current)
      closeTimeout.current = null
    }
    setMegaOpen(false)
  }, [])

  const scheduleCloseMega = useCallback(() => {
    if (closeTimeout.current) return
    closeTimeout.current = setTimeout(() => {
      setMegaOpen(false)
      closeTimeout.current = null
    }, 120)
  }, [])

  const cancelCloseMega = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current)
      closeTimeout.current = null
    }
  }, [])

  // Explore dropdown close helpers
  const closeExplore = useCallback(() => {
    if (exploreCloseTimeout.current) {
      clearTimeout(exploreCloseTimeout.current)
      exploreCloseTimeout.current = null
    }
    setExploreOpen(false)
  }, [])

  const scheduleCloseExplore = useCallback(() => {
    if (exploreCloseTimeout.current) return
    exploreCloseTimeout.current = setTimeout(() => {
      setExploreOpen(false)
      exploreCloseTimeout.current = null
    }, 120)
  }, [])

  const cancelCloseExplore = useCallback(() => {
    if (exploreCloseTimeout.current) {
      clearTimeout(exploreCloseTimeout.current)
      exploreCloseTimeout.current = null
    }
  }, [])

  useEffect(() => {
    if (!megaOpen) return
    const handleKey = (e) => { if (e.key === 'Escape') closeMega() }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [megaOpen, closeMega])

  useEffect(() => {
    if (!exploreOpen) return
    const handleKey = (e) => { if (e.key === 'Escape') closeExplore() }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [exploreOpen, closeExplore])

  useEffect(() => {
    closeMega()
    closeExplore()
    setMobileOpen(false)
  }, [location.pathname, closeMega, closeExplore])

  useEffect(() => () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current)
    if (exploreCloseTimeout.current) clearTimeout(exploreCloseTimeout.current)
  }, [])

  const handleNav = (id) => {
    if (document.getElementById(id)) {
      if (onNavigate) onNavigate(id)
      else document.getElementById(id).scrollIntoView({ behavior: 'smooth' })
    } else {
      navigate(`/explore#${id}`)
    }
    setMobileOpen(false)
    closeExplore()
  }

  const linkBase = 'text-xs uppercase tracking-[0.16em] xl:tracking-[0.18em] font-sans font-medium transition-colors duration-300 min-h-[44px] inline-flex items-center whitespace-nowrap'
  const linkColor = navOverHero
    ? 'text-white/80 hover:text-cinema-gold'
    : 'text-cinema-soft hover:text-cinema-gold'
  const activeColor = 'text-cinema-gold'

  return (
    <>
      {/* ── Desktop nav ─────────────────────────────────────── */}
      <nav
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-700 ${
          scrolled ? 'cinema-glass-strong !border-b-0 py-0' : 'bg-transparent py-2'
        }`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-14">
          <div className="relative flex items-center justify-between h-16 sm:h-20 lg:h-24 gap-4">

            {/* Logo & Academy Name */}
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-3 group shrink-0 mr-2 xl:mr-12"
            >
              <img
                src="/Logos/SMA LOGO  DARK MODE.png"
                alt="Synergy Multimedia Academy"
                className="h-10 sm:h-12 transition-transform duration-500 group-hover:scale-105"
              />
              <div className="hidden sm:block">
                <p className="font-display text-sm tracking-tight leading-tight text-white whitespace-nowrap">Synergy Multimedia</p>
                <p className="text-xs uppercase tracking-[0.25em] text-cinema-gold font-medium">Academy</p>
              </div>
            </Link>

            {/* Main Navigation Links — Programs · Masterclass (with date) · Explore */}
            <div className="hidden lg:flex items-center gap-5 xl:gap-7 2xl:gap-10 min-[1800px]:absolute min-[1800px]:left-1/2 min-[1800px]:-translate-x-1/2">
              {/* Programs */}
              <Link
                to={intakeHref}
                className={`${linkBase} ${linkColor}`}
                aria-label={publicIntakeLabel ? `${publicIntakeLabel} — view available programs` : 'Browse all programs'}
              >
                Programs
              </Link>

              {/* Masterclass trigger — date merged inline */}
              <div className="relative" ref={megaRef}>
                <button
                  ref={megaBtnRef}
                  onMouseEnter={() => {
                    cancelCloseMega()
                    setMegaOpen(true)
                  }}
                  onMouseLeave={(e) => {
                    const related = e.relatedTarget
                    if (megaRef.current?.contains(related) || megaMenuRef.current?.contains(related)) return
                    scheduleCloseMega()
                  }}
                  onClick={() => setMegaOpen((v) => !v)}
                  aria-expanded={megaOpen}
                  aria-haspopup="true"
                  className={`${linkBase} gap-1 ${isMasterclassSection ? activeColor : linkColor}`}
                >
                  Masterclass
                  <MasterclassDateInline upcoming={navUpcoming} />
                  <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${megaOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Project Lab — Learn by creating */}
              <Link
                to="/project-lab"
                className={`${linkBase} ${isProjectLabSection ? activeColor : linkColor}`}
                aria-label="Project Lab — learn by creating"
              >
                Project Lab
              </Link>

              {/* Explore dropdown — Academy, Mentors, Showcase, Contact */}
              <div className="relative" ref={exploreRef}>
                <button
                  ref={exploreBtnRef}
                  onMouseEnter={() => {
                    cancelCloseExplore()
                    setExploreOpen(true)
                  }}
                  onMouseLeave={(e) => {
                    const related = e.relatedTarget
                    if (exploreRef.current?.contains(related) || exploreMenuRef.current?.contains(related)) return
                    scheduleCloseExplore()
                  }}
                  onClick={() => setExploreOpen((v) => !v)}
                  aria-expanded={exploreOpen}
                  aria-haspopup="true"
                  className={`${linkBase} gap-1 ${isExploreSection ? activeColor : linkColor}`}
                >
                  Explore
                  <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${exploreOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>

            {/* Right Action Cluster: Talk to advisor · Sign in — Enroll (with intake subtext) */}
            <div className="hidden lg:flex items-center gap-4 xl:gap-7 shrink-0 ml-auto">
              {/* Talk to advisor — plain text link, no border/background */}
              <Link
                to="/advisor"
                className={`hidden xl:inline text-xs font-sans font-medium tracking-[0.14em] uppercase whitespace-nowrap transition-colors duration-300 ${
                  navOverHero ? 'text-white/70 hover:text-white' : 'text-cinema-muted hover:text-white'
                }`}
              >
                Talk to advisor
              </Link>

              {/* Sign in — smaller, lower-contrast text */}
              <Link
                to="/login"
                className={`text-[11px] font-sans font-medium tracking-[0.16em] uppercase whitespace-nowrap transition-colors duration-300 ${
                  navOverHero ? 'text-white/50 hover:text-cinema-gold' : 'text-cinema-muted/60 hover:text-cinema-gold'
                }`}
              >
                Sign in
              </Link>

              {/* Enroll — the only filled button in the bar, with inline intake badge */}
              <Link
                to="/register"
                className="cinema-btn-primary !px-5 xl:!px-6 !py-2.5 !min-h-[40px] !text-xs shrink-0 font-medium inline-flex items-center gap-2 whitespace-nowrap"
              >
                Enroll
                {intakeSubtext && (
                  <span className="hidden min-[1800px]:inline-flex items-center gap-1.5 border-l border-white/25 pl-2 text-[11px] font-semibold tracking-wide uppercase opacity-90">
                    <span className="relative flex h-2 w-2 shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
                    </span>
                    {intakeSubtext}
                  </span>
                )}
              </Link>
            </div>

            {/* Mobile: Enroll stays visible next to the hamburger */}
            <div className="lg:hidden flex items-center gap-1 ml-auto">
              <Link
                to="/register"
                className="cinema-btn-primary !px-4 !py-2 !min-h-[40px] !text-xs"
              >
                Enroll
              </Link>
              <button
                onClick={() => setMobileOpen(true)}
                className="p-3 min-w-[48px] min-h-[48px] text-white"
                aria-label="Open navigation menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Mega menu — lives inside nav so mouse hover works */}
        <div
          ref={megaMenuRef}
          onMouseEnter={() => {
            cancelCloseMega()
            setMegaOpen(true)
          }}
          onMouseLeave={() => scheduleCloseMega()}
        >
          <MasterclassMegaMenu visible={megaOpen} onClose={closeMega} upcoming={navUpcoming} />
        </div>

        {/* Explore dropdown panel */}
        <div
          ref={exploreMenuRef}
          onMouseEnter={() => {
            cancelCloseExplore()
            setExploreOpen(true)
          }}
          onMouseLeave={() => scheduleCloseExplore()}
        >
          {exploreOpen && (
            <div
              className="absolute top-full left-0 right-0 transition-all duration-300"
              style={{ zIndex: 100 }}
            >
              <div className="h-px bg-gradient-to-r from-transparent via-cinema-gold/30 to-transparent" />
              <div className="w-full bg-cinema-charcoal border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
                  <div className="grid md:grid-cols-3 gap-8">
                    {EXPLORE_GROUPS.map((group) => (
                      <div key={group.label}>
                        <p className="text-xs uppercase tracking-[0.25em] text-cinema-gold mb-4 font-sans font-semibold">
                          {group.label}
                        </p>
                        <div className="space-y-1">
                          {group.links.map(({ icon: Icon, label, desc, id, to }) => {
                            const content = (
                              <>
                                <div className="mt-0.5 p-2 shrink-0 bg-white/5 text-cinema-muted group-hover:bg-cinema-glow/15 group-hover:text-cinema-glow transition-colors">
                                  <Icon className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="font-sans text-sm font-medium text-white group-hover:text-cinema-gold transition-colors">
                                    {label}
                                  </p>
                                  <p className="text-cinema-muted font-sans text-xs mt-0.5 leading-snug">{desc}</p>
                                </div>
                              </>
                            )
                            return id ? (
                              <button
                                key={label}
                                onClick={() => handleNav(id)}
                                className="group flex items-start gap-4 p-3 transition-colors duration-200 hover:bg-white/5 text-left w-full"
                              >
                                {content}
                              </button>
                            ) : (
                              <Link
                                key={label}
                                to={to}
                                onClick={closeExplore}
                                className="group flex items-start gap-4 p-3 transition-colors duration-200 hover:bg-white/5"
                              >
                                {content}
                              </Link>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Click-outside overlay for mega menu */}
      {megaOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={closeMega}
          aria-hidden="true"
        />
      )}
      {/* Click-outside overlay for explore dropdown */}
      {exploreOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={closeExplore}
          aria-hidden="true"
        />
      )}

      {/* ── Mobile drawer ────────────────────────────────────── */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-500 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-sm bg-cinema-charcoal border-l border-white/5 overflow-y-auto transition-transform duration-500 ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex justify-between items-center px-8 py-6 border-b border-white/5">
            <span className="cinema-label">Menu</span>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2 min-w-[48px] min-h-[48px] text-white flex items-center justify-center"
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="px-8 py-4">
            {/* Programs */}
            <Link
              to={intakeHref}
              onClick={() => setMobileOpen(false)}
              className="block py-4 min-h-[56px] text-lg font-display font-light text-white border-b border-white/5 hover:text-cinema-gold transition-colors"
            >
              Programs
            </Link>

            {/* Masterclass accordion — date merged inline */}
            <div className="border-b border-white/5">
              <button
                onClick={() => setMcMobileOpen((v) => !v)}
                className={`w-full flex items-center justify-between py-4 min-h-[56px] text-lg font-display font-light transition-colors ${
                  isMasterclassSection ? 'text-cinema-gold' : 'text-white hover:text-cinema-gold'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  Masterclass
                  <MasterclassDateInline upcoming={navUpcoming} />
                </span>
                <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${mcMobileOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Accordion body */}
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  mcMobileOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="pb-4 space-y-1">
                  {MASTERCLASS_LINKS.map(({ icon: Icon, label, to, cta }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 py-3 px-4 text-sm font-sans transition-colors rounded-none ${
                        cta
                          ? 'text-cinema-glow bg-cinema-glow/10 hover:bg-cinema-glow/15'
                          : 'text-cinema-soft hover:text-cinema-gold hover:bg-white/3'
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Project Lab */}
            <Link
              to="/project-lab"
              onClick={() => setMobileOpen(false)}
              className={`block py-4 min-h-[56px] text-lg font-display font-light border-b border-white/5 transition-colors ${
                isProjectLabSection ? 'text-cinema-gold' : 'text-white hover:text-cinema-gold'
              }`}
            >
              Project Lab
            </Link>

            {/* Explore accordion — Academy, Mentors, Showcase, Contact */}
            <div className="border-b border-white/5">
              <button
                onClick={() => setExploreMobileOpen((v) => !v)}
                className={`w-full flex items-center justify-between py-4 min-h-[56px] text-lg font-display font-light transition-colors ${
                  isExploreSection ? 'text-cinema-gold' : 'text-white hover:text-cinema-gold'
                }`}
              >
                <span>Explore</span>
                <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${exploreMobileOpen ? 'rotate-180' : ''}`} />
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  exploreMobileOpen ? 'max-h-[720px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="pb-4 space-y-4">
                  {EXPLORE_GROUPS.map((group) => (
                    <div key={group.label}>
                      <p className="px-4 pb-1 text-[0.65rem] uppercase tracking-[0.2em] text-cinema-gold font-sans">{group.label}</p>
                      {group.links.map(({ icon: Icon, label, id, to }) => {
                        const content = (
                          <>
                            <Icon className="w-4 h-4 shrink-0 text-cinema-muted" />
                            <span>{label}</span>
                          </>
                        )
                        return id ? (
                          <button
                            key={label}
                            onClick={() => handleNav(id)}
                            className="w-full flex items-center gap-3 py-3 px-4 text-sm font-sans text-cinema-soft hover:text-cinema-gold hover:bg-white/3 transition-colors rounded-none text-left"
                          >
                            {content}
                          </button>
                        ) : (
                          <Link
                            key={label}
                            to={to}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-3 py-3 px-4 text-sm font-sans text-cinema-soft hover:text-cinema-gold hover:bg-white/3 transition-colors rounded-none"
                          >
                            {content}
                          </Link>
                        )
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Talk to advisor — plain text link */}
            <Link
              to="/advisor"
              onClick={() => setMobileOpen(false)}
              className="block py-4 min-h-[56px] text-lg font-display font-light text-cinema-muted border-b border-white/5 hover:text-white transition-colors"
            >
              Talk to advisor
            </Link>
          </div>

          <div className="px-8 py-6 space-y-3">
            <Link to="/register" onClick={() => setMobileOpen(false)} className="cinema-btn-primary w-full inline-flex items-center justify-center gap-2">
              Enroll
              {intakeSubtext && (
                <span className="inline-flex items-center gap-1.5 border-l border-white/25 pl-2 text-[11px] font-semibold tracking-wide uppercase opacity-90">
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
                  </span>
                  {intakeSubtext}
                </span>
              )}
            </Link>
            <Link to="/login" onClick={() => setMobileOpen(false)} className="block text-center text-sm text-cinema-muted hover:text-cinema-gold transition-colors font-sans">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}

export function BackToTop({ visible }) {
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Back to top"
      className={`fixed bottom-24 right-6 z-40 p-3 cinema-glass text-cinema-muted hover:text-cinema-gold transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
      </svg>
    </button>
  )
}
